import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Loader2, AlertCircle, Download, ExternalLink } from 'lucide-react';
/**
 * Public ticket viewer.
 *
 * URL: /ticket/:orderNumber
 *
 * Purpose: Provide a SHORT, predictable, on-domain URL for the kitchen ticket
 * image. WhatsApp on some Android phones occasionally mis-parses very long
 * Cloudinary URLs, causing the link to open the bare host. By hosting the
 * viewer on noizacr.com we eliminate that failure mode for restaurant owners.
 */
export function TicketPage() {
  const {
    orderNumber
  } = useParams<{
    orderNumber: string;
  }>();
  const [ticketUrl, setTicketUrl] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retried, setRetried] = useState(0);
  useEffect(() => {
    let mounted = true;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    async function load() {
      if (!orderNumber) {
        if (mounted) {
          setError('Número de orden no encontrado');
          setLoading(false);
        }
        return;
      }
      try {
        const {
          data,
          error: fetchErr
        } = await supabase.from('orders').select('ticket_image_url, restaurants(name)').eq('order_number', orderNumber).maybeSingle();
        if (fetchErr) throw fetchErr;
        if (!data) {
          if (mounted) {
            setError('No encontramos este pedido');
            setLoading(false);
          }
          return;
        }
        const url = (data as any).ticket_image_url as string | null;
        const rName = (data as any).restaurants?.name || null;
        if (!mounted) return;
        if (url) {
          setTicketUrl(url);
          setRestaurantName(rName);
          setLoading(false);
        } else if (retried < 6) {
          // Ticket image upload is async — retry every 2.5s up to 6 times (~15s).
          timeout = setTimeout(() => {
            if (mounted) setRetried(c => c + 1);
          }, 2500);
        } else {
          setRestaurantName(rName);
          setError('El ticket aún se está generando. Vuelve a intentar en unos segundos.');
          setLoading(false);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err?.message || 'Error al cargar el ticket');
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      mounted = false;
      if (timeout) clearTimeout(timeout);
    };
  }, [orderNumber, retried]);
  const handleDownload = () => {
    if (!ticketUrl) return;
    // Force download on mobile by opening in new tab. The user can long-press
    // to save the image. Direct attribute "download" is unreliable cross-origin.
    window.open(ticketUrl, '_blank', 'noopener,noreferrer');
  };
  if (loading) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6" data-id="element-8875" data-mp="14vfpxnkkinjp">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" data-id="element-8876" data-mp="zwuyodnane7e" />
        <p className="text-sm text-gray-500" data-id="element-8877" data-mp="zypa91codtyt">Cargando ticket…</p>
        {retried > 0 && <p className="text-xs text-gray-400 mt-1" data-id="element-8878" data-mp="1dvku7ougt5tn">
            Generando ticket… intento {retried}/6
          </p>}
      </div>;
  }
  if (error || !ticketUrl) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6" data-id="element-8879" data-mp="l8863a8nopy8">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg" data-id="element-8880" data-mp="h7ejck6y9h6x">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" data-id="element-8881" data-mp="uyjn9yhcovn2" />
          <h1 className="text-xl font-black text-gray-900 mb-1" data-id="element-8882" data-mp="q71kd4vrokzg">
            Ticket no disponible
          </h1>
          <p className="text-sm text-gray-500 mb-4" data-id="element-8883" data-mp="2gfglpsspmv9">
            {error || 'No pudimos encontrar este ticket.'}
          </p>
          {orderNumber && <p className="text-xs text-gray-400 mb-4" data-id="element-8884" data-mp="3tlnwlun5zfel">
              Orden: <span className="font-mono" data-id="element-8885" data-mp="b65vcqgabhm1">{orderNumber}</span>
            </p>}
          <button onClick={() => {
          setError(null);
          setLoading(true);
          setRetried(0);
        }} className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-700 transition-colors" data-id="element-8886" data-mp="g4zccsus5qqy">
            Reintentar
          </button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gray-100 flex flex-col items-center py-6 px-4" data-id="element-8887" data-mp="yu5n9tpc1e27">
      <div className="w-full max-w-md" data-id="element-8888" data-mp="13tijv6609zhz">
        {/* Header */}
        <div className="bg-white rounded-t-2xl px-5 py-4 border-b border-gray-100 shadow-sm" data-id="element-8889" data-mp="13u1u00pezjlg">
          <h1 className="text-base font-black text-gray-900" data-id="element-8890" data-mp="18s3aqezdb6tr">
            🧾 Ticket del pedido
          </h1>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-8891" data-mp="35mpvo5metyz4">
            {restaurantName ? `${restaurantName} · ` : ''}
            <span className="font-mono" data-id="element-8892" data-mp="3alt4m15igpcd">{orderNumber}</span>
          </p>
        </div>

        {/* Image */}
        <div className="bg-white shadow-sm flex items-center justify-center p-3" data-id="element-8893" data-mp="1my58x985huf5">
          <img src={ticketUrl} alt={`Ticket ${orderNumber}`} className="w-full h-auto rounded-md" decoding="async" data-id="element-8894" data-mp="1mw0wg2w6rn39" />
        </div>

        {/* Actions */}
        <div className="bg-white rounded-b-2xl px-5 py-4 border-t border-gray-100 shadow-sm flex flex-col gap-2" data-id="element-8895" data-mp="1dvm9z07hk1y6">
          <button onClick={handleDownload} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-gray-700 transition-colors" data-id="element-8896" data-mp="26me0pi5s7ye4">
            <Download className="w-4 h-4" data-id="element-8897" data-mp="1wn5bt3prx9ym" />
            Abrir / Descargar imagen
          </button>
          <a href={ticketUrl} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors" data-id="element-8898" data-mp="q8nxedmhle5j">
            <ExternalLink className="w-4 h-4" data-id="element-8899" data-mp="rmxkqm0zli87" />
            Ver en nueva pestaña
          </a>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-4" data-id="element-8900" data-mp="drlf3jlam7ci">
          Servido por NOIZA
        </p>
      </div>
    </div>;
}